import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarCheck, UserCheck, CreditCard, Headphones, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-phone-support.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const TopWaysAIPhoneSupport = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.topWaysAIPhoneSupport.metaTitle')}</title>
        <meta name="description" content={t('blog.topWaysAIPhoneSupport.metaDescription')} />
        <meta name="keywords" content="AI phone support use cases, AI appointment scheduling, AI lead qualification, AI payment processing, AI tools for small business call handling" />
        <link rel="canonical" href="https://call24x7.ai/blog/top-ways-businesses-use-ai-phone-support" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:title" content={t('blog.topWaysAIPhoneSupport.title')} />
        <meta property="article:published_time" content="2025-12-06" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.topWaysAIPhoneSupport.title'),
            "description": t('blog.topWaysAIPhoneSupport.metaDescription'),
            "author": {"@type": "Organization", "name": "Call24x7.AI"},
            "publisher": {"@type": "Organization", "name": "Call24x7.AI"},
            "datePublished": "2025-12-06",
            "mainEntityOfPage": "https://call24x7.ai/blog/top-ways-businesses-use-ai-phone-support"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.topWaysAIPhoneSupport.title')}
        category={t('blog.categories.useCases')}
        date="December 6, 2025"
        readTime={`5 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI phone support for business"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.topWaysAIPhoneSupport.intro')}
        </p>

        <Card className="p-6 my-8 border-primary/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <CalendarCheck className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">1. {t('blog.topWaysAIPhoneSupport.way1Title')}</h2>
              <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.way1Desc')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.bestFor')}:</strong> {t('blog.topWaysAIPhoneSupport.way1BestFor')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.impact')}:</strong> {t('blog.topWaysAIPhoneSupport.way1Impact')}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 my-8 border-primary/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <UserCheck className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">2. {t('blog.topWaysAIPhoneSupport.way2Title')}</h2>
              <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.way2Desc')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.bestFor')}:</strong> {t('blog.topWaysAIPhoneSupport.way2BestFor')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.impact')}:</strong> {t('blog.topWaysAIPhoneSupport.way2Impact')}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 my-8 border-primary/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <CreditCard className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">3. {t('blog.topWaysAIPhoneSupport.way3Title')}</h2>
              <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.way3Desc')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.bestFor')}:</strong> {t('blog.topWaysAIPhoneSupport.way3BestFor')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.impact')}:</strong> {t('blog.topWaysAIPhoneSupport.way3Impact')}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 my-8 border-primary/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Headphones className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">4. {t('blog.topWaysAIPhoneSupport.way4Title')}</h2>
              <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.way4Desc')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.bestFor')}:</strong> {t('blog.topWaysAIPhoneSupport.way4BestFor')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.impact')}:</strong> {t('blog.topWaysAIPhoneSupport.way4Impact')}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 my-8 border-primary/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <MessageSquare className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">5. {t('blog.topWaysAIPhoneSupport.way5Title')}</h2>
              <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.way5Desc')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.bestFor')}:</strong> {t('blog.topWaysAIPhoneSupport.way5BestFor')}</p>
              <p><strong>{t('blog.topWaysAIPhoneSupport.impact')}:</strong> {t('blog.topWaysAIPhoneSupport.way5Impact')}</p>
            </div>
          </div>
        </Card>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.topWaysAIPhoneSupport.choosingTitle')}</h2>
        <p>{t('blog.topWaysAIPhoneSupport.choosingDesc1')}</p>
        <p className="mt-4">{t('blog.topWaysAIPhoneSupport.choosingDesc2')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.topWaysAIPhoneSupport.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.topWaysAIPhoneSupport.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.topWaysAIPhoneSupport.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default TopWaysAIPhoneSupport;
